import { useState, useEffect, useMemo, useCallback } from "react";
import type { HubSpotProduct } from "../types/configurator";
import { fetchProducts } from "../api/hubspot";
import { buildProductMap, groupByCategory } from "../data/products";

export function useHubSpotProducts() {
  const [products, setProducts] = useState<HubSpotProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await fetchProducts();
      setProducts(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load products');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const productMap = useMemo(() => buildProductMap(products), [products]);
  const categories = useMemo(() => groupByCategory(products), [products]);

  const search = (query: string) => {
    if (!query.trim()) return categories;

    const q = query.toLowerCase();
    const filtered: Record<string, HubSpotProduct[]> = {};

    for (const [cat, prods] of Object.entries(categories)) {
      const matches = prods.filter(p =>
        p.name.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q)
      );
      if (matches.length > 0) filtered[cat] = matches;
    }

    return filtered;
  };

  return {
    products,
    productMap,
    categories,
    loading,
    error,
    refetch: loadProducts,
    search,
  };
}
